import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { SESSION_EXPIRED_EVENT, retrySessionRenewal, getPendingSessionRequestCount } from './API';

// only access-token writes matter — intermediate adal keys (error, login.request, …) would trigger premature attempts
const ADAL_TOKEN_KEY_PREFIX = 'adal.access.token.key';

// above adal's loadFrameTimeout (5s in AdalConfig): a real attempt settles first; if adal never invokes the callback, the guard frees the button
const RENEW_ATTEMPT_TIMEOUT_MS = 10000;

export default class SessionExpiredModal extends Component {

    state = {
        isOpen: false,
        checking: false,
        attemptFailed: false
    };

    componentDidMount() {
        this._mounted = true;
        window.addEventListener(SESSION_EXPIRED_EVENT, this.handleSessionExpired);
        window.addEventListener('storage', this.handleStorage);
        // 'focus' and 'visibilitychange' overlap on purpose — browsers fire either or both depending on how
        // the user returns (tab switch vs window switch); while an attempt is in flight the renewing flag
        // blocks the duplicate, and afterwards it is only a cache-first acquireToken no-op
        window.addEventListener('focus', this.handleReturnToTab);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        if (getPendingSessionRequestCount() > 0) {
            this.handleSessionExpired();
        }
    }

    componentWillUnmount() {
        this._mounted = false;
        window.removeEventListener(SESSION_EXPIRED_EVENT, this.handleSessionExpired);
        window.removeEventListener('storage', this.handleStorage);
        window.removeEventListener('focus', this.handleReturnToTab);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    handleSessionExpired = () => {
        // the renewal tab handles the login itself; the modal only exists in the original tab
        if (window.location.pathname.toLowerCase().endsWith('/sessionrenewal')) {
            return;
        }
        if (!this.state.isOpen) {
            this.setState({ isOpen: true, attemptFailed: false });
        }
    }

    handleStorage = (e) => {
        if (this.state.isOpen && e.key && e.key.startsWith(ADAL_TOKEN_KEY_PREFIX)) {
            this.tryRenew(false);
        }
    }

    handleVisibilityChange = () => {
        if (!document.hidden) {
            this.handleReturnToTab();
        }
    }

    handleReturnToTab = () => {
        if (this.state.isOpen) {
            this.tryRenew(false);
        }
    }

    openLoginTab = () => {
        // repeated clicks reuse the tab that is already open — a new window.open would orphan the old handle
        if (this.loginTab && !this.loginTab.closed) {
            this.loginTab.focus();
            return;
        }
        const base = document.getElementsByTagName('base')[0]?.getAttribute('href') || '/';
        this.loginTab = window.open(`${base.replace(/\/+$/, '')}/SessionRenewal`, '_blank');
    }

    // window.close() inside the login tab is blocked after the AAD round-trip (cross-origin history); the opener may always close it
    closeLoginTab = () => {
        try {
            if (this.loginTab && !this.loginTab.closed) {
                this.loginTab.close();
            }
        } catch (e) { /* cross-origin handle — ignore */ }
        this.loginTab = null;
    }

    tryRenew = (manual) => {
        if (this.renewing) {
            return;
        }
        this.renewing = true;
        this.setState({ checking: true });

        let raceSettled = false;

        try {
            const attempt = retrySessionRenewal();
            // late close: only acts when the guard already expired (raceSettled) and the real renewal completed afterwards — the queue was already resumed
            attempt.then(renewed => {
                if (renewed && raceSettled && this._mounted && this.state.isOpen) {
                    this.closeLoginTab();
                    this.setState({ isOpen: false, checking: false, attemptFailed: false });
                }
            }).catch(() => { });

            const attemptTimeout = new Promise(resolve => setTimeout(() => resolve(false), RENEW_ATTEMPT_TIMEOUT_MS));
            Promise.race([attempt, attemptTimeout])
                .catch(() => false)
                .then(renewed => {
                    raceSettled = true;
                    this.renewing = false;
                    if (!this._mounted) {
                        return;
                    }
                    if (renewed) {
                        this.closeLoginTab();
                        this.setState({ isOpen: false, checking: false, attemptFailed: false });
                    } else {
                        this.setState(prev => ({ checking: false, attemptFailed: manual ? true : prev.attemptFailed }));
                    }
                });
        } catch (e) {
            // nothing above is expected to throw synchronously, but a stuck flag would disable renewal forever
            this.renewing = false;
            if (this._mounted) {
                this.setState({ checking: false });
            }
        }
    }

    render() {
        const { isOpen, checking, attemptFailed } = this.state;

        // zIndex above react-block-ui's vendor default (.block-ui-message-container is 10001): a parked save keeps the screen's BlockUi active under the modal
        return (
            <Modal isOpen={isOpen} zIndex={100000} fade={false} backdrop="static" keyboard={false} className="modal-md" style={{ minWidth: 0, marginTop: '15%' }}>
                <ModalBody className="text-center py-4">
                    <i className="fas fa-user-lock mb-3" style={{ fontSize: '28px' }} />
                    <h5 className="mb-3"><FormattedMessage id="SessionExpiredModal.Title" /></h5>
                    <p className="mb-2"><FormattedMessage id="SessionExpiredModal.Explanation" /></p>
                    <p className="mb-0"><FormattedMessage id="SessionExpiredModal.Instructions" /></p>
                    {attemptFailed ?
                        <p className="text-danger mt-3 mb-0"><FormattedMessage id="SessionExpiredModal.StillExpired" /></p>
                    : ''}
                </ModalBody>
                <ModalFooter className="py-2 border-0 justify-content-center">
                    <Button className="btn btn-sm btn-host mr-2" onClick={this.openLoginTab}>
                        <FormattedMessage id="SessionExpiredModal.OpenLoginTab" />
                    </Button>
                    <Button className="btn btn-sm" onClick={() => this.tryRenew(true)}>
                        {checking ? <i className="fas fa-circle-notch fa-spin mr-1" /> : ''}
                        <FormattedMessage id="SessionExpiredModal.CheckAgain" />
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }
}
